import { Box, Typography, useTheme } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { INK } from "../../../../theme/tokens";
import type { OpenShiftRequest } from "../../../../utils/models";
import { useUpdateNursesNeeded } from "../../../RequestData/hooks";
import { EyebrowLabel } from "../../../UIToolkit/molecules/EyebrowLabel";
import { InlineHint } from "../../../UIToolkit/molecules/InlineHint";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { GapsQuantityHero, QUANTITY_HERO_FIGURE_SX } from "./GapsQuantityHero";
import { GapsPostedStep } from "./gapsPostedSteps";

type Props = {
	request: OpenShiftRequest;
	/** `Nurse 7a-7p · Thu 1 Oct` — the shift being changed. */
	context: string;
	onStep: (step: GapsPostedStep) => void;
};

const SHEET = "gaps-posted-needed";

/**
 * How many people a posted shift still needs (P3-10), nudged in the post sheet's own stepper card (`GapsQuantityHero`)
 * and written with `useUpdateNursesNeeded` — the Requests screen's "staff needed" editor. `needed_nurses` is what is
 * still OPEN (every approval takes one off), so that is the number shown and set; the classic post screen's 1–100
 * range bounds it.
 */
export const GapsPostedNeededStep: FC<Props> = ({ request, context, onStep }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { mutateAsync: updateNeededNurses, isPending } = useUpdateNursesNeeded({});
	const [needed, setNeeded] = useState<number>(request.needed_nurses);

	const properties = useMemo(
		() => ({ department_id: chosenDepartment.department_id, request_id: request.id, from_needed: request.needed_nurses, to_needed: needed }),
		[chosenDepartment.department_id, request.id, request.needed_nurses, needed],
	);

	const handleSave = useCallback(async (): Promise<void> => {
		track("gaps_posted_needed_start", properties);
		try {
			await updateNeededNurses({ shiftId: request.id, neededNurses: needed });
			track("gaps_posted_needed_success", properties);
			onStep(GapsPostedStep.Actions);
		} catch (error) {
			// The mutation's own `onError` has shown the snackbar; this records where from, and the step stays for a retry.
			trackError("gaps_posted_needed_error", { ...properties, error_message: error instanceof Error ? error.message : String(error) });
		}
	}, [needed, onStep, properties, request.id, track, trackError, updateNeededNurses]);

	return (
		<SheetStep
			stepKey={GapsPostedStep.Needed}
			dataTestId={SHEET}
			title={t("gaps.postedSheet.neededTitle")}
			subtitle={context}
			footer={
				<>
					<SheetStepButton tone="dismiss" onClick={() => onStep(GapsPostedStep.Actions)} disabled={isPending} dataTestId={`${SHEET}-cancel`}>
						{t("back")}
					</SheetStepButton>
					<SheetStepButton
						tone="confirm"
						onClick={() => void handleSave()}
						disabled={needed === request.needed_nurses}
						loading={isPending}
						dataTestId={`${SHEET}-confirm`}
					>
						{t("gaps.postedSheet.neededSave")}
					</SheetStepButton>
				</>
			}
		>
			<GapsQuantityHero
				ink={theme.palette.primary.main}
				dataTestId={`${SHEET}-hero`}
				label={t("gaps.postedSheet.neededLabel")}
				stepperLabel={t("gaps.postedSheet.neededLabel")}
				value={needed}
				onChange={setNeeded}
				disabled={isPending}
				stepperDataTestId={`${SHEET}-stepper`}
				aside={
					<>
						<EyebrowLabel>{t("gaps.postedSheet.neededWas")}</EyebrowLabel>
						<Typography data-testid={`${SHEET}-was`} sx={{ ...QUANTITY_HERO_FIGURE_SX, color: INK.MUTED }}>
							{request.needed_nurses}
						</Typography>
					</>
				}
			/>
			<Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
				<InlineHint tone="info" dataTestId={`${SHEET}-note`}>
					{t("gaps.postedSheet.neededNote")}
				</InlineHint>
			</Box>
		</SheetStep>
	);
};
